import { useMemo } from "react";
import { object } from "prop-types";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { generateResourceId } from "@repo/structure";
import {
  TimelineSectionSettings,
  useEstimateEditorSettings,
  useEstimateTableSettings,
  useTableExpandHandler
} from "@tools";
import { AddButtonFull, TooltipIcon } from "@components";
import { Badge, Collapse, Grid } from "@material-ui/core";
import { TimelineTeamMemberRow } from "./TimelineTeamMemberRow";
import { ChildActions, TimelineSectionInner } from "./components";
import { TimelineSectionsRowGroupList } from "./TimelineSectionsRowGroupList";
import { CELL_WIDTHS, getPercentWorkTypes } from "@utils";
import { makeStyles } from "@material-ui/styles";
import classnames from "classnames";
import { Percent } from "@assets";

export const TimelineWorkTypeRow = observer(({ workType, section }) => {
  const { t } = useTranslation();
  const { allowEdition, blockExpansions, isPdfPrinterView } = useEstimateEditorSettings();
  const { fullSectionActions } = useEstimateTableSettings();
  const classes = useStyle({
    useDragHandleCell: !isPdfPrinterView,
    isWider: fullSectionActions,
  });

  const {
    name,
    color,
    fontColor,
    expanded: workTypeExpanded,
    visibleTeamMembers,
    percentageWorkTypes,
    cellAutomatedWorkTypes,
  } = workType;

  const expandable = useMemo(() => (
    allowEdition || visibleTeamMembers.length
  ), [ allowEdition, visibleTeamMembers ]);

  const expanded = useMemo(() => (
    expandable && (workTypeExpanded || blockExpansions)
  ), [ workTypeExpanded, blockExpansions, expandable ]);

  const percentWorkTypes = useMemo(() => (
    getPercentWorkTypes(percentageWorkTypes)
  ), [ percentageWorkTypes ]);

  const automatedWorkTypes = useMemo(() => (
    getPercentWorkTypes(cellAutomatedWorkTypes.of)
  ), [ cellAutomatedWorkTypes ]);

  const { handleOpen, ...collapseProps } = useTableExpandHandler({
    expandable: expandable,
    isOpened: workTypeExpanded,
    onExpand: workType.setExpanded,
  });

  const handleTeaMemberAdd = () => {
    workType.addTeamMember(generateResourceId())
  }

  return (
    <Grid
      item
      container
      style={{ opacity: workType.isTurnedOff ? 0.3 : undefined }}
    >
      <Grid item container>
        <TimelineSectionInner
          name={name}
          color={color}
          fontColor={fontColor}
          onColorChange={workType.setColor}
          onFontColorChange={workType.setFontColor}
          tableDepth={3}
          expandable={expandable}
          onExpand={handleOpen}
          actions={
            <ChildActions
              workType={workType}
              fullActions={fullSectionActions}
              showActions={!expanded || !visibleTeamMembers.length}
            />
          }
        >
          {
            allowEdition && percentWorkTypes.length > 0 &&
            <TooltipIcon icon={<Percent />} color="primary" className="noDrag text-sm p-0-5">
              {percentWorkTypes}
            </TooltipIcon>
          }
          {
            allowEdition && automatedWorkTypes.length > 0 &&
            <Badge
              color="secondary"
              badgeContent={cellAutomatedWorkTypes.counter}
              overlap="circular"
              max={9}
              anchorOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              classes={{
                badge: "w-12 h-12 text-2xs"
              }}
            >
              <TooltipIcon icon={<Percent />} color="secondary" className="noDrag text-sm p-0-5">
                {t("views.editor.timeline_table.automated_cell_work_types", { workTypes: automatedWorkTypes })}
              </TooltipIcon>
            </Badge>
          }
          {
            allowEdition &&
            <TimelineSectionSettings section={section} />
          }
        </TimelineSectionInner>
      </Grid>
      <Collapse
        in={expanded}
        timeout="auto"
        mountOnEnter
        unmountOnExit
        className="w-full"
        {...collapseProps}
      >
        <TimelineSectionsRowGroupList
          listElements={visibleTeamMembers}
          onReorder={workType.reorderTeamMembers}
          disabled={!allowEdition}
          group={`teamMembers-${workType.id}-${section.id}`}
        >
          {visibleTeamMembers.map((teamMember, index) => (
            <TimelineTeamMemberRow
              key={teamMember.id}
              teamMember={teamMember}
              workType={workType}
              section={section}
              previousTeamMember={visibleTeamMembers[index - 1]}
              nextTeamMember={visibleTeamMembers[index + 1]}
            />
          ))}
        </TimelineSectionsRowGroupList>
        <Grid
          item container
          className={classnames("bg-secondary preset-page display-overflow", classes.addButtonContainer)}
        >
          {
            allowEdition &&
            <AddButtonFull
              color="secondary"
              onClick={handleTeaMemberAdd}
            >
              {t("views.editor.timeline_table.work_resource_add")}
            </AddButtonFull>
          }
        </Grid>
      </Collapse>
    </Grid>
  );
});

TimelineWorkTypeRow.propTypes = {
  workType: object.isRequired,
  section: object.isRequired,
};

const useStyle = makeStyles(() => ({
  addButtonContainer: {
    minWidth: ({ useDragHandleCell, isWider }) => (isWider ? CELL_WIDTHS.TITLE_LARGE : CELL_WIDTHS.TITLE_NORMAL) + (useDragHandleCell ? CELL_WIDTHS.DRAG_HANDLE : 0),
    maxWidth: ({ useDragHandleCell, isWider }) => (isWider ? CELL_WIDTHS.TITLE_LARGE : CELL_WIDTHS.TITLE_NORMAL) + (useDragHandleCell ? CELL_WIDTHS.DRAG_HANDLE : 0),
    paddingLeft: ({ useDragHandleCell }) => CELL_WIDTHS.EXPAND.DEPTH_3 + (useDragHandleCell ? CELL_WIDTHS.DRAG_HANDLE : 0),
    zIndex: 4,
  }
}));
